/**
 * Banner on Historical Financial Analysis when the practitioner arrives from
 * an indicator's "View supporting financials" (MOR-1280): names the
 * indicator and the evidence period (highlighted in the tables), says so
 * when that period was not extracted or is in another granularity, and
 * links back to the assessment.
 */

import { ArrowLeft, Crosshair, X } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import type { EvidenceFocus } from "./types";

interface EvidenceFocusBannerProps {
  focus: EvidenceFocus;
  /** Whether `focus.periodLabel` matched an extracted period. */
  periodFound: boolean;
  /** Set when the matched period sits in a granularity not being shown. */
  periodGranularityLabel?: string;
  onShowPeriod?: () => void;
}

/** Renders the evidence-focus banner described in the file overview. */
export function EvidenceFocusBanner({
  focus,
  periodFound,
  periodGranularityLabel,
  onShowPeriod,
}: EvidenceFocusBannerProps) {
  const subject = focus.indicatorName
    ? `Supporting financials for ${focus.indicatorName}`
    : "Supporting financials";
  const detail = !focus.periodLabel
    ? "The assessment's evidence does not cite a specific period."
    : !periodFound
      ? `The evidence cites ${focus.periodLabel}, which is not among the extracted periods.`
      : periodGranularityLabel
        ? `The evidence cites ${focus.periodLabel}, shown with the ${periodGranularityLabel.toLowerCase()} periods.`
        : `The evidence cites ${focus.periodLabel}, highlighted below.`;

  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-violet-200 bg-violet-50/60 px-4 py-2.5 dark:border-violet-900 dark:bg-violet-950/20"
    >
      <Crosshair className="h-3.5 w-3.5 shrink-0 text-violet-600 dark:text-violet-400" />
      <p className="min-w-0 flex-[1_1_16rem] text-xs">
        <span className="font-semibold">{subject}.</span>{" "}
        <span className="text-muted-foreground">{detail}</span>
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        {periodGranularityLabel && onShowPeriod && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={onShowPeriod}
          >
            Show {periodGranularityLabel.toLowerCase()} periods
          </Button>
        )}
        {focus.onReturn && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={focus.onReturn}
          >
            <ArrowLeft className="h-3 w-3" />
            Back to assessment
          </Button>
        )}
        {focus.onDismiss && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            aria-label="Dismiss supporting financials focus"
            onClick={focus.onDismiss}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
